import Grid from "@mui/material/Grid/Grid";
import type {ColDef} from "ag-grid-community/dist/lib/entities/colDef";
import {AgGridReact} from "ag-grid-react";
import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useAgGridLocale} from "../../../../../../config/globalConfig";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import {useExcessGroupRepository} from "../../../../api/repositories/ExcessGroupRepository";
import {useExcessRepository} from "../../../../api/repositories/ExcessRepository";
import {
    ExcessGroupAssociationRequest,
    ExcessGroupMatrix,
    ExcessGroupMatrixRecord,
    FormExcessGroupModel
} from "../../../../api/types/excessGroup";
import {SelectElementModelFE} from "../../../../api/types/generic";
import CustomBanner from "../../../../common/components/CustomBanner";
import {excessGroupCreationAtom} from "../../../../state/excessGroupsRecoil";
import {useMatrixColDefs} from "./useMatrixColDefs";
import {BASE_DEFINITION_FORM_STEPS, DEFAULT_MATRIX_COL_DEF} from "../../../../constants";
import NumberCustomEditorComponent from "../../../../common/components/NumberCustomEditorComponent";
import {useAcrissRepository} from "../../../../api/repositories/AcrissRepository";
import {ProcessCellForExportParams} from "ag-grid-community";

const ExcessGroupMatrixDefinition = ({submitRef}: { submitRef: RefObject<HTMLButtonElement> }) => {
    // Use State
    const [matrix, setMatrix] = useState<ExcessGroupMatrix>([]);
    const [excesses, setExcesses] = useState<SelectElementModelFE[]>([]);
    const [carGroups, setCarGroups] = useState<SelectElementModelFE[]>([]);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // use Recoil
    const [excessGroupCreation, setExcessGroupCreation] = useRecoilState(excessGroupCreationAtom);
    const {groupId, readOnly, inUse, matrixData, newId} = excessGroupCreation;

    const columnDefs: ColDef[] = useMatrixColDefs({matrixData: matrixData || [], excesses, editable: !(readOnly), t});
    const agGridLocale = useAgGridLocale();

    // Use Repository Hooks
    const {getExcesses} = useExcessRepository();
    const {updateExcessMatrix} = useExcessGroupRepository();
    const {getOrderedAcriss} = useAcrissRepository();

    // use ref
    const gridRef = useRef<AgGridReact<ExcessGroupMatrixRecord>>(null);

    useEffect(() => {
        getStaticData();
        // eslint-disable-next-line
    }, []);

    const getStaticData = async () => {
        try {
            const excesses = await getExcesses();
            // togliere CAR_GROUPS || quando si pusha
            //  const carGroups = CAR_GROUPS || await getOrderedAcriss()
            const carGroups = await getOrderedAcriss((excessGroupCreation.definitionData as FormExcessGroupModel)?.legalEntity?.code as string);

            setExcesses((excesses).map((excess) => ({code: excess.id, label: excess.name})));
            setCarGroups((carGroups).map((carGroup) => ({code: carGroup.id, label: carGroup.acriss})));
        } catch (e) {
        }
    };

    const normalize = useCallback(
        (carGroups: SelectElementModelFE[]): Record<string, number[]> => {
            const carGroupsCodes = carGroups.map((c) => c.label);
            return carGroupsCodes.reduce(
                (acc, code) => ({
                    ...acc,
                    [code]: matrixData
                        ?.filter((detail) => detail.id.carGroup === code)
                        ?.map((detail) => detail.id.excessId)
                }),
                {}
            );
        },
        [matrixData]
    );

    const matrixInit = useCallback(() => {
        if (carGroups?.length && matrixData?.length) {
            const normalizedCarGroups = normalize(carGroups);

            const init = carGroups.reduce<ExcessGroupMatrix>((acc, carGroup) => {
                if (normalizedCarGroups[carGroup.label]?.length) {
                    const obj: ExcessGroupMatrixRecord = {
                        carGroup: carGroup.label,
                        excessGroupId: groupId as number
                    };
                    normalizedCarGroups[carGroup.label].forEach((excessId) => {
                        const excess = excesses.find((excess) => excess.code === excessId);
                        if (excess) {
                            const detailFound = matrixData.filter(
                                (detail) => detail.id.carGroup === carGroup.label && detail.id.excessId === excessId
                            )[0];
                            obj[excess.label] = {
                                ...detailFound,
                                amount: detailFound.amount / 100
                            };
                        }
                    });

                    return [...acc, obj];
                }
                return [...acc];
            }, []);

            setMatrix(init);
        }
    }, [carGroups, excesses, matrixData, normalize, groupId]);

    useEffect(() => {
        matrixInit();
    }, [matrixInit]);

    const matrixConfirm = () => {
        if (newId) {
            const request: ExcessGroupAssociationRequest = {excessGroupId: newId, incomingDetail: []};
            gridRef.current?.api.forEachNode((node) => {
                if (node.data) {
                    const row = node.data;
                    Object.values(row).map((value) => typeof value === "object" && request.incomingDetail.push({
                        ...value,
                        amount: value.amount * 100
                    }));
                }
            });

            try {
                updateExcessMatrix(request);
                setExcessGroupCreation({...excessGroupCreation, step: BASE_DEFINITION_FORM_STEPS.DONE});
            } catch (e) {
            }
        }
    };

    const defaultColDef = {...DEFAULT_MATRIX_COL_DEF};

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value)) {
            return params.value;
        } else {
            return "";
        }
    }

    return (
        <Grid container spacing={2}>
            {inUse && <CustomBanner message={t("pricing:excessGroup.banner.matrix")} severity={"warning"}/>}
            <Grid item xs={12} sm={12}>
                <AgGridCustomTable
                    rowData={matrix}
                    defaultColDef={defaultColDef}
                    domLayout={"autoHeight"}
                    tooltipShowDelay={0}
                    frameworkComponents={{
                        customEditor: NumberCustomEditorComponent
                    }}
                    columnDefs={columnDefs}
                    gridRef={gridRef}
                    localeText={agGridLocale}
                    pagination={false}
                    stopEditingWhenCellsLoseFocus
                    rowSelection={"multiple"}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    enableCellTextSelection={false}
                    processCellFromClipboard={processCellFromClipboard}
                />
            </Grid>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    matrixConfirm();
                }}
            >
                <button ref={submitRef} type="submit" style={{display: "none"}}/>
            </form>
        </Grid>
    );
};

export default ExcessGroupMatrixDefinition;
